import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X, ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { api, CATEGORY_NAMES } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { PageHeader } from "../components/PageHeader";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const PRICE_MAX = 10000;

const SORTS = [
  ["recommended", "Recomandate"],
  ["price_asc", "Preț crescător"],
  ["price_desc", "Preț descrescător"],
  ["name", "Alfabetic"],
];

const Check = ({ checked, label, onChange, testId, count }) => (
  <button data-testid={testId} onClick={onChange} className="flex items-center justify-between w-full py-2 text-sm group">
    <span className="flex items-start gap-3 text-left">
      <span className={`mt-0.5 h-4 w-4 shrink-0 border flex items-center justify-center transition-colors duration-200 ${checked ? "bg-[#0A0A0A] border-[#0A0A0A]" : "border-black/30 group-hover:border-[#0A0A0A]"}`}>
        {checked && <span className="h-1.5 w-1.5 bg-white" />}
      </span>
      <span className={checked ? "text-[#0A0A0A]" : "text-[#4A4A4A]"}>{label}</span>
    </span>
    {count !== undefined && <span className="text-xs text-[#9A9A9A]">{count}</span>}
  </button>
);

const Filters = ({ all, state, set, categorySlug }) => {
  const collections = useMemo(() => [...new Set(all.map((p) => p.collection))].sort(), [all]);
  const activities = useMemo(() => [...new Set(all.flatMap((p) => p.activities || []))].sort(), [all]);
  const toggle = (key, v) => set({ ...state, [key]: state[key].includes(v) ? state[key].filter((x) => x !== v) : [...state[key], v] });

  return (
    <div className="space-y-10">
      {!categorySlug && (
        <div>
          <p className="eyebrow mb-3">Categorie</p>
          {Object.entries(CATEGORY_NAMES).map(([slug, name]) => (
            <Check key={slug} testId={`filter-category-${slug}`} label={name} checked={state.categories.includes(slug)} onChange={() => toggle("categories", slug)} count={all.filter((p) => p.category === slug).length} />
          ))}
        </div>
      )}
      <div>
        <p className="eyebrow mb-3">Colecție</p>
        {collections.map((c) => (
          <Check key={c} testId={`filter-collection-${c.replace(/\s|\//g, "-").toLowerCase()}`} label={c} checked={state.collections.includes(c)} onChange={() => toggle("collections", c)} count={all.filter((p) => p.collection === c).length} />
        ))}
      </div>
      {activities.length > 0 && (
        <div>
          <p className="eyebrow mb-3">Activitate</p>
          {activities.map((a) => (
            <Check key={a} testId={`filter-activity-${a.toLowerCase()}`} label={a} checked={state.activities.includes(a)} onChange={() => toggle("activities", a)} />
          ))}
        </div>
      )}
      <div>
        <p className="eyebrow mb-3">Număr copii</p>
        <Check testId="filter-children-1" label="1 copil" checked={state.children.includes(1)} onChange={() => toggle("children", 1)} />
        <Check testId="filter-children-2" label="2 copii" checked={state.children.includes(2)} onChange={() => toggle("children", 2)} />
      </div>
      <div>
        <div className="flex justify-between items-baseline mb-4">
          <p className="eyebrow">Preț</p>
          <p data-testid="filter-price-label" className="text-xs text-[#4A4A4A]">{state.price[0].toLocaleString("ro-RO")} – {state.price[1].toLocaleString("ro-RO")} lei</p>
        </div>
        <Slider data-testid="filter-price-slider" min={0} max={PRICE_MAX} step={50} value={state.price} onValueChange={(v) => set({ ...state, price: v })} className="[&_[role=slider]]:rounded-none [&_[role=slider]]:border-[#0A0A0A] [&_[role=slider]]:bg-white [&_.bg-primary]:bg-[#0A0A0A]" />
      </div>
    </div>
  );
};

const EMPTY = { categories: [], collections: [], activities: [], children: [], price: [0, PRICE_MAX] };

export default function Catalog() {
  const { category } = useParams();
  const [params] = useSearchParams();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recommended");
  const [state, setState] = useState(EMPTY);
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: category ? { category } : {} }).then((r) => { setAll(r.data); setLoading(false); });
    const init = { ...EMPTY };
    if (params.get("activity")) init.activities = [params.get("activity")];
    if (params.get("children")) init.children = [Number(params.get("children"))];
    if (params.get("stage") === "newborn") init.collections = ["Sleek", "Spring", "Charm"];
    setState(init);
  }, [category, params]);

  const products = useMemo(() => {
    let list = all.filter((p) => p.price >= state.price[0] && p.price <= state.price[1]);
    if (state.categories.length) list = list.filter((p) => state.categories.includes(p.category));
    if (state.collections.length) list = list.filter((p) => state.collections.includes(p.collection));
    if (state.activities.length) list = list.filter((p) => (p.activities || []).some((a) => state.activities.includes(a)));
    if (state.children.length) list = list.filter((p) => state.children.includes(p.children));
    const sorters = {
      price_asc: (a, b) => a.price - b.price,
      price_desc: (a, b) => b.price - a.price,
      name: (a, b) => a.name.localeCompare(b.name),
      recommended: (a, b) => Number(b.featured) - Number(a.featured) || b.price - a.price,
    };
    return [...list].sort(sorters[sort]);
  }, [all, state, sort]);

  const activeCount = state.categories.length + state.collections.length + state.activities.length + state.children.length + (state.price[0] > 0 || state.price[1] < PRICE_MAX ? 1 : 0);
  const title = category ? CATEGORY_NAMES[category] : "Tot catalogul";
  const descriptions = {
    carucioare: "Cărucioare compacte și modulare pentru oraș: Sleek, Spring 2, Charm 2 în 1 și seturile cu landou.",
    sport: "Pentru alergare și terenuri variate: Urban Glide 4 și pachetul complet Urban Glide 3.",
    multisport: "Remorci de bicicletă Chariot care devin cărucioare de jogging și plimbare în câteva secunde.",
    "scaune-auto": "Scaune auto Thule Maple, Elm, Palm și baza ISOFIX Thule Alfi.",
    purtare: "Rucsacul de transport copii Thule Sapling, pentru drumeții în familie.",
  };

  return (
    <div data-testid="catalog-page">
      <PageHeader eyebrow="Colecție THULE" title={title} description={category ? descriptions[category] : "Toate cărucioarele, remorcile și scaunele auto THULE din selecția Cassia Concept."} crumbs={[{ label: "Catalog", to: "/catalog" }, ...(category ? [{ label: title }] : [])]} testId="catalog-header" />

      <div className="container-cc">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-6 border-b border-black/10 mb-8">
          <Link to="/catalog" data-testid="chip-all" className={`shrink-0 h-9 px-4 inline-flex items-center text-[12px] uppercase tracking-[0.14em] font-semibold border transition-colors duration-200 ${!category ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/15 hover:border-[#0A0A0A]"}`}>Toate</Link>
          {Object.entries(CATEGORY_NAMES).map(([slug, name]) => (
            <Link key={slug} to={`/catalog/${slug}`} data-testid={`chip-${slug}`} className={`shrink-0 h-9 px-4 inline-flex items-center text-[12px] uppercase tracking-[0.14em] font-semibold border transition-colors duration-200 ${category === slug ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/15 hover:border-[#0A0A0A]"}`}>{name}</Link>
          ))}
        </div>

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button data-testid="filters-open-btn" onClick={() => setDrawer(true)} className="lg:hidden inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.14em] font-semibold">
              <SlidersHorizontal size={16} strokeWidth={1.5} /> Filtre {activeCount > 0 && `(${activeCount})`}
            </button>
            <p data-testid="results-count" className="text-sm text-[#737373] whitespace-nowrap">{products.length} {products.length === 1 ? "produs" : "produse"}</p>
            {activeCount > 0 && <button data-testid="filters-reset" onClick={() => setState(EMPTY)} className="text-xs uppercase tracking-[0.14em] underline underline-offset-4">Resetează</button>}
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="sort-select" className="w-[200px] h-10 rounded-none border-black/15 text-[12px] uppercase tracking-[0.14em] font-semibold focus:ring-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-white">
              {SORTS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`sort-option-${v}`} className="text-sm">{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="hidden lg:block col-span-3 xl:col-span-2" data-testid="filters-sidebar">
            <div className="sticky top-[132px]"><Filters all={all} state={state} set={setState} categorySlug={category} /></div>
          </aside>
          <div className="lg:col-span-9 xl:col-span-10 min-w-0">
            {loading ? (
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-12">
                {Array.from({ length: 6 }).map((_, i) => <div key={i} className="surface aspect-[4/5] animate-pulse" />)}
              </div>
            ) : products.length === 0 ? (
              <div data-testid="catalog-empty" className="surface py-24 text-center">
                <p className="font-serif text-3xl">Niciun produs nu corespunde filtrelor</p>
                <p className="text-sm text-[#737373] mt-2">Încearcă să relaxezi criteriile sau resetează filtrele.</p>
                <button onClick={() => setState(EMPTY)} className="btn-outline mt-8">Resetează filtrele</button>
              </div>
            ) : (
              <div data-testid="product-grid" className="grid grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-12">
                {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {drawer && (
          <>
            <motion.div className="fixed inset-0 bg-black/40 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDrawer(false)} />
            <motion.aside data-testid="filters-drawer" className="fixed inset-y-0 right-0 w-[86vw] max-w-sm bg-white z-50 flex flex-col" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", stiffness: 300, damping: 30 }}>
              <div className="h-[72px] px-5 flex items-center justify-between border-b border-black/10">
                <p className="font-serif text-2xl">Filtre</p>
                <button data-testid="filters-drawer-close" aria-label="Închide" className="icon-btn" onClick={() => setDrawer(false)}><X size={20} strokeWidth={1.5} /></button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-6"><Filters all={all} state={state} set={setState} categorySlug={category} /></div>
              <div className="p-5 border-t border-black/10"><button onClick={() => setDrawer(false)} className="btn-primary w-full">Vezi {products.length} {products.length === 1 ? "produs" : "produse"}</button></div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
      <ChevronDown className="hidden" />
    </div>
  );
}
